import { Badge, Button, Checkbox, Label } from "@beweegbaas/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useState } from "react";
import { Page } from "#/components/page";
import { LayoutAuth } from "#/layouts/layout.auth";
import { markOnboardingSeen } from "#/modules/generic.user/user.functions";
import { m } from "#/paraglide/messages";

export const Route = createFileRoute("/$locale/{-$schoolyear}/_authenticated/$school/onboarding")({
  // Opt out of the `$school` DefaultLayout chrome.
  staticData: { layout: false },
  component: Onboarding,
});

/**
 * Login guard for schools without leerlingen: every login lands here until the CTA is pressed
 * (see `getLoginRedirect`). The CTA marks the onboarding as seen (backend-tracked) and continues
 * into the import wizard.
 */
function Onboarding() {
  const navigate = Route.useNavigate();
  const markSeenFn = useServerFn(markOnboardingSeen);
  const [understood, setUnderstood] = useState(false);

  const onContinue = async () => {
    await markSeenFn({});
    await navigate({
      to: "/$locale/{-$schoolyear}/$school/detail/import/$type",
      params: (prev) => ({ ...prev, type: "modify-existing-year" }),
    });
  };

  return (
    <LayoutAuth showVisuals={false} containerClassName="max-w-[55ch]!">
      <div className="grid justify-items-start gap-6">
        <Page.Title>{m["onboarding.title"]()}</Page.Title>
        <div className="prose grid gap-4">
          <p>{m["onboarding.intro"]()}</p>
        </div>

        <div className="grid gap-4">
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium">{m["onboarding.beweegdoelen_title"]()}</p>
              <Badge variant="green" size="sm">
                <IconMaterialSymbolsCheckCircleRounded />
                {m["onboarding.badge_migrated"]()}
              </Badge>
            </div>
            <p className="text-muted-foreground text-sm">
              {m["onboarding.beweegdoelen_description"]()}
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium">{m["onboarding.resultaten_title"]()}</p>
              <Badge variant="gray" size="sm">
                <IconMaterialSymbolsSchedule />
                {m["onboarding.badge_follows_automatically"]()}
              </Badge>
            </div>
            <p className="text-muted-foreground text-sm">
              {m["onboarding.resultaten_description"]()}
            </p>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium">{m["onboarding.import_students"]()}</p>
              <Badge variant="orange" size="sm">
                <IconMaterialSymbolsErrorRounded />
                {m["onboarding.badge_action_required"]()}
              </Badge>
            </div>
            <p className="text-muted-foreground text-sm">
              {m["onboarding.import_students_description"]()}
            </p>
          </div>
          <hr className="mt-4 mb-3" />
        </div>

        <div className="flex items-center gap-3">
          <Checkbox
            id="onboarding-understood"
            name="onboarding-understood"
            checked={understood}
            onCheckedChange={(v) => setUnderstood(v === true)}
          />
          <Label htmlFor="onboarding-understood">{m["onboarding.understood_label"]()}</Label>
        </div>

        <Button size="xl" disabled={!understood} onClick={() => void onContinue()}>
          {m["onboarding.import_students"]()}
          <IconMaterialSymbolsArrowRightAltRounded />
        </Button>
      </div>
    </LayoutAuth>
  );
}
